import { useState } from 'react'
import { Sparkles, Wand2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { generateTestData, DT_STR as S, type GenerateResult } from './devtoolsApi'

const ENTITIES: { id: string; label: string }[] = [
  { id: 'users', label: S.entUsers },
  { id: 'chats', label: S.entChats },
  { id: 'messages', label: S.entMessages },
  { id: 'bots', label: S.entBots },
]

/**
 * A27.09 — Генератор данных. Seeds fake records for QA: pick an entity + count,
 * hit generate → `POST /admin/devtools/generate {entity, count}`. Functional
 * (posts when the endpoint ships; toasts the outcome). Endpoint enforces access.
 */
export function DataGeneratorScreen() {
  const { toast } = useToast()
  const [entity, setEntity] = useState('users')
  const [count, setCount] = useState(10)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<GenerateResult | null>(null)

  const generate = async () => {
    setBusy(true)
    setResult(null)
    try {
      const res = await generateTestData(entity, count)
      setResult(res)
      toast(S.generated, { tone: 'success' })
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.dataGenerator} screenId="A27.09" />
      <div className="max-w-xl space-y-4 p-4">
        <div className="space-y-3 rounded-md border border-border-default bg-surface-1 p-4">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-secondary">{S.entity}</span>
            <select
              value={entity}
              onChange={(e) => setEntity(e.target.value)}
              className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong"
            >
              {ENTITIES.map((ent) => (
                <option key={ent.id} value={ent.id}>
                  {ent.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-secondary">{S.count}</span>
            <input
              type="number"
              min={1}
              max={10000}
              value={count}
              onChange={(e) => setCount(Math.max(1, Number(e.target.value) || 1))}
              className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong"
            />
          </label>
          <button
            type="button"
            onClick={generate}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            <Wand2 size={14} />
            {busy ? S.generating : S.generate}
          </button>
        </div>
        {result && (
          <div className="flex items-center gap-2 rounded-md border border-border-default bg-surface-1 px-4 py-3 text-sm text-primary">
            <Sparkles size={16} className="text-green-500" />
            <span>
              {S.result}: {result.created}
            </span>
          </div>
        )}
      </div>
    </>
  )
}
